// System health (D-077): what players try to do and how it ends, how fast the server and the app are, and what breaks.
// The server records every /api request against a named flow (or its route) as done, refused or failed; the client
// sends page speed (Web Vitals), app timings, connection drops and crashes. Admin › Health reads it all.
import { z } from "zod";
/** The things players do that matter most, in display order. A request that matches none is counted by its route. */
export const FLOWS = [
    { id: "sign-in", label: "Sign in", group: "Getting in", method: "POST", route: "/api/login" },
    { id: "sign-up", label: "Sign up", group: "Getting in", method: "POST", route: "/api/register" },
    { id: "session", label: "Open the app", group: "Getting in", method: "GET", route: "/api/session" },
    { id: "create-table", label: "Create a table", group: "Tables", method: "POST", route: "/api/rooms" },
    { id: "join-table", label: "Join with a code or link", group: "Tables", method: "POST", route: "/api/rooms/join" },
    { id: "play-online", label: "Play online", group: "Tables", method: "POST", route: "/api/play-online" },
    { id: "ranked", label: "Find a ranked game", group: "Tables", method: "POST", route: "/api/ranked" },
    { id: "open-table", label: "Load a table", group: "Tables", method: "GET", route: "/api/rooms/:code" },
    { id: "club-table", label: "Open a club table", group: "Tables", method: "POST", route: "/api/clubs/:code/tables" },
    { id: "club-queue", label: "Queue at a club table", group: "Tables", method: "POST", route: "/api/clubs/:code/tables/:room/queue" },
    { id: "club-watch", label: "Watch a club table", group: "Tables", method: "POST", route: "/api/clubs/:code/tables/:room/watch" },
    { id: "move-draw", label: "Draw or take", group: "Moves", method: "POST", route: "/api/rooms/:code/act", ops: ["draw"] },
    { id: "move-discard", label: "Discard", group: "Moves", method: "POST", route: "/api/rooms/:code/act", ops: ["discard"] },
    { id: "move-declare", label: "Declare DIGU", group: "Moves", method: "POST", route: "/api/rooms/:code/act", ops: ["declare"] },
    { id: "move-ready", label: "Ready and start", group: "Moves", method: "POST", route: "/api/rooms/:code/act", ops: ["ready", "start", "online-start", "find-more"] },
    { id: "move-time", label: "Add time", group: "Moves", method: "POST", route: "/api/rooms/:code/act", ops: ["timeBank"] },
    { id: "move-next", label: "Next game", group: "Moves", method: "POST", route: "/api/rooms/:code/act", ops: ["rematch", "resume"] },
    { id: "move-leave", label: "Leave a table", group: "Moves", method: "POST", route: "/api/rooms/:code/act", ops: ["leave"] },
    { id: "move-settings", label: "Table settings", group: "Moves", method: "POST", route: "/api/rooms/:code/act" },
    { id: "chat", label: "Table chat", group: "Moves", method: "POST", route: "/api/rooms/:code/chat" },
    { id: "react", label: "Reactions", group: "Moves", method: "POST", route: "/api/rooms/:code/react" },
    { id: "boli-transfer", label: "Send Boli", group: "Money", method: "POST", route: "/api/boli/transfer" },
    { id: "shop-purchase", label: "Buy in the shop", group: "Money", method: "POST", route: "/api/shop/purchase" },
    { id: "shop-daily", label: "Daily reward", group: "Money", method: "POST", route: "/api/shop/daily" },
    { id: "shop-equip", label: "Equip an item", group: "Money", method: "POST", route: "/api/shop/equip" },
    { id: "table-invite", label: "Invite to a table", group: "Friends and clubs", method: "POST", route: "/api/rooms/:code/invite" },
    { id: "invite-decline", label: "Decline an invite", group: "Friends and clubs", method: "POST", route: "/api/invites/:code/decline" },
    { id: "friend-request", label: "Friend request", group: "Friends and clubs", method: "POST", route: "/api/friends", ops: ["request"] },
    { id: "friend-accept", label: "Accept a friend", group: "Friends and clubs", method: "POST", route: "/api/friends", ops: ["accept"] },
    { id: "friends-other", label: "Other friend actions", group: "Friends and clubs", method: "POST", route: "/api/friends" },
    { id: "club-join", label: "Ask to join a club", group: "Friends and clubs", method: "POST", route: "/api/clubs", ops: ["join"] },
    { id: "club-accept", label: "Accept into a club", group: "Friends and clubs", method: "POST", route: "/api/clubs", ops: ["accept"] },
    { id: "club-create", label: "Create a club", group: "Friends and clubs", method: "POST", route: "/api/clubs", ops: ["create"] },
    { id: "clubs-other", label: "Other club actions", group: "Friends and clubs", method: "POST", route: "/api/clubs" },
    { id: "tournament-register", label: "Register for a tournament", group: "Friends and clubs", method: "POST", route: "/api/tournaments/:id/register" },
    { id: "tournament-checkin", label: "Tournament check-in", group: "Friends and clubs", method: "POST", route: "/api/tournaments/:id/checkin" },
];
/** The body discriminator a flow is matched on: a table move's `type`, a club or friend `op`; a declaring discard is "declare". */
export function opOf(body) {
    if (!body || typeof body !== "object")
        return null;
    const b = body;
    if (b.type === "discard" && b.declare === true)
        return "declare";
    return typeof b.type === "string" ? b.type : typeof b.op === "string" ? b.op : null;
}
export function flowOf(method, route, body) {
    const op = opOf(body);
    for (const f of FLOWS) {
        if (f.method !== method || f.route !== route)
            continue;
        if (!f.ops || (op !== null && f.ops.includes(op)))
            return f;
    }
    return null;
}
/* ---------- Client telemetry ---------- */
export const VITALS = ["LCP", "INP", "CLS", "FCP", "TTFB"];
/** Google's Web Vitals thresholds: at or under `good` is good, over `poor` is poor. CLS has no unit; the rest are ms. */
export const VITAL_THRESHOLDS = {
    LCP: { good: 2500, poor: 4000, label: "Main content shown", what: "Largest Contentful Paint: when the biggest thing on the page has appeared." },
    INP: { good: 200, poor: 500, label: "Response to taps", what: "Interaction to Next Paint: the slowest time from a tap to the screen reacting." },
    CLS: { good: 0.1, poor: 0.25, label: "Layout shifting", what: "Cumulative Layout Shift: how much things jump around while loading (no unit)." },
    FCP: { good: 1800, poor: 3000, label: "First thing shown", what: "First Contentful Paint: when anything first appears." },
    TTFB: { good: 800, poor: 1800, label: "Server first byte", what: "Time to First Byte: how long the server took to start answering." },
};
export const TIMINGS = ["table-ready", "ws-connect", "ws-reconnect"];
export const TIMING_LABELS = {
    "table-ready": "Open a table until the cards show",
    "ws-connect": "Live connection opens",
    "ws-reconnect": "Back online after a drop",
};
export const CLIENT_COUNTS = ["ws-drop", "network-fail", "shape"];
export const ClientEvent = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("vital"), name: z.enum(VITALS), value: z.number().min(0).max(600_000), route: z.string().min(1).max(120) }),
    z.object({ kind: z.literal("timing"), name: z.enum(TIMINGS), value: z.number().min(0).max(600_000), route: z.string().min(1).max(120) }),
    z.object({ kind: z.literal("count"), name: z.enum(CLIENT_COUNTS), route: z.string().min(1).max(120) }),
    z.object({
        kind: z.literal("error"),
        source: z.enum(["boundary", "window", "promise", "api"]),
        message: z.string().min(1).max(300),
        stack: z.string().max(2000).optional(),
        route: z.string().min(1).max(120),
        /** The server's reference, when an API call failed. */
        ref: z.string().max(12).optional(),
        status: z.number().int().min(0).max(599).optional(),
    }),
]);
export const ClientBatch = z.object({
    /** The web build the page runs (a timestamp baked in at build time). */
    build: z.string().max(40),
    vw: z.number().int().min(200).max(8000),
    /** navigator.connection.effectiveType when the browser has it ("4g", "3g"). */
    conn: z.string().max(12).optional(),
    events: z.array(ClientEvent).min(1).max(40),
});
/* ---------- Admin › Health views ---------- */
export const HEALTH_WINDOWS = ["1h", "24h", "7d", "30d"];
//# sourceMappingURL=health.js.map